import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import HeroSlideImage, { FALLBACK_SLIDE } from './HeroSlideImage';
import './HeroSlider.css';

const HeroSlider = ({ slides = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Fallback cuando la API no devuelve slides: solo diapositiva (slider0)
  const defaultSlides = [
    {
      id: 1,
      title: '',
      subtitle: '',
      image: FALLBACK_SLIDE,
      imageCandidates: [FALLBACK_SLIDE],
      backgroundPosition: 'center 0%',
      link: '/clero'
    }
  ];

  const slidesToShow = slides.length > 0 ? slides : defaultSlides;

  // Auto-play del slider (también en móvil)
  useEffect(() => {
    if (!isAutoPlaying || slidesToShow.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slidesToShow.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, slidesToShow.length]);

  // Reanudar auto-play cuando la pestaña vuelve a ser visible (móvil)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') setIsAutoPlaying(true);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000); // Reanudar auto-play después de 8 segundos
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slidesToShow.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slidesToShow.length) % slidesToShow.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const handlePrev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    prevSlide();
  };

  const handleNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    nextSlide();
  };

  const handleIndicator = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    goToSlide(index);
  };

  // Móvil: asegurar que taps en controles no sean capturados por el Link del slide
  const handleControlTouch = (e, handler) => {
    e.preventDefault();
    e.stopPropagation();
    handler();
  };

  const totalSlides = slidesToShow.length;
  const showControls = totalSlides > 1;

  return (
    <div
      className="hero-slider"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => totalSlides > 1 && setIsAutoPlaying(true)}
      onFocusCapture={() => setIsAutoPlaying(false)}
      onBlurCapture={() => totalSlides > 1 && setIsAutoPlaying(true)}
    >
      <div className="slider-container">
        {slidesToShow.map((slide, index) => {
          const isActive = index === currentSlide;
          const slideContent = (
            <div
              className={`slide ${isActive ? 'active' : ''}`}
              role="img"
              aria-label={slide.title || `Slide ${index + 1}`}
            >
              <HeroSlideImage
                image={slide.image}
                imageCandidates={slide.imageCandidates}
                alt={slide.title || `Diapositiva ${index + 1}`}
                isActive={isActive}
              />
              <div className="slide-overlay"></div>
            </div>
          );
          let link = (slide.link || '').trim();
          // Corregir enlace incorrecto: /noticias/1 → /convocatorias/8 (convocatoria activa)
          if (link === '/noticias/1') link = '/convocatorias/8';
          const isInternalLink = link && link.startsWith('/') && !link.startsWith('//');
          const isExternalLink = link && (link.startsWith('http://') || link.startsWith('https://'));
          if (isInternalLink) {
            return (
              <Link
                key={slide.id || index}
                to={link}
                className={`slide-link-wrapper${isActive ? ' is-active' : ''}`}
                aria-hidden={!isActive}
                tabIndex={isActive ? 0 : -1}
              >
                {slideContent}
              </Link>
            );
          }
          if (isExternalLink) {
            return (
              <a
                key={slide.id || index}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className={`slide-link-wrapper${isActive ? ' is-active' : ''}`}
                aria-hidden={!isActive}
                tabIndex={isActive ? 0 : -1}
              >
                {slideContent}
              </a>
            );
          }
          return (
            <div key={slide.id || index} className={`slide-link-wrapper${isActive ? ' is-active' : ''}`}>
              {slideContent}
            </div>
          );
        })}
      </div>

      {showControls && (
        <>
          <button
            className="slider-nav slider-prev"
            type="button"
            onClick={handlePrev}
            onTouchEnd={(e) => handleControlTouch(e, prevSlide)}
            aria-label="Diapositiva anterior"
          >
            <span className="slider-nav-icon" aria-hidden>
              <FaChevronLeft />
            </span>
          </button>
          <button
            className="slider-nav slider-next"
            type="button"
            onClick={handleNext}
            onTouchEnd={(e) => handleControlTouch(e, nextSlide)}
            aria-label="Diapositiva siguiente"
          >
            <span className="slider-nav-icon" aria-hidden>
              <FaChevronRight />
            </span>
          </button>

          <div className="hero-slider-controls" role="group" aria-label="Navegación del carrusel">
            <div className="slider-indicators">
              {slidesToShow.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentSlide ? 'active' : ''}`}
                  type="button"
                  onClick={(e) => handleIndicator(e, index)}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleIndicator(e, index);
                  }}
                  aria-label={`Ir a la diapositiva ${index + 1} de ${totalSlides}`}
                  aria-current={index === currentSlide ? 'true' : undefined}
                />
              ))}
            </div>
            <span className="slider-counter" aria-live="polite">
              {currentSlide + 1}
              <span className="slider-counter-sep">/</span>
              {totalSlides}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default HeroSlider;







